import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from "../assets/images/elayone.png";
import donate from "../assets/images/donate.png";
import search from "../assets/images/search.png";
import useNavMenu from '../hooks/useNavMenu';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { links: NAV_LINKS, cta } = useNavMenu();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <header className={`fixed top-0 w-full z-50 transition-all duration-500 ease-in-out
        ${isScrolled
          ? 'bg-black/95 backdrop-blur-sm py-3 shadow-[0_1px_0_rgba(255,255,255,0.08)]'
          : 'bg-gradient-to-b from-black/60 to-transparent py-5'}`}>
        <div className="max-w-screen-xl mx-auto flex items-center justify-between px-6 lg:px-10">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0 group">
            <img alt="Elayone Music" className="w-[52px] md:w-[62px] invert transition-opacity duration-200 group-hover:opacity-75"
              src={logo} loading="eager" decoding="async" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
            {NAV_LINKS.map(({ link, label, menu_key }) => {
              const isActive = location.pathname === link;
              return (
                <Link key={menu_key} to={link}
                  className={`relative text-[12px] xl:text-[13px] tracking-[0.2em] uppercase font-medium
                    transition-colors duration-200
                    after:absolute after:bottom-[-3px] after:left-0 after:h-[1px] after:bg-white
                    after:transition-all after:duration-300
                    ${isActive ? 'text-white after:w-full' : 'text-gray-400 hover:text-white after:w-0 hover:after:w-full'}`}>
                  {label}
                </Link>
              );
            })}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-4 xl:gap-5">
            <Link to="/music" aria-label="Search"
              className="text-gray-400 hover:text-white transition-colors duration-200 p-1">
              <img alt="Search" className="w-[18px] md:w-[20px] opacity-70 hover:opacity-100 transition-opacity"
                src={search} loading="eager" decoding="async" />
            </Link>

            <ThemeToggle style={{ color: 'rgba(255,255,255,0.7)' }} />

            {cta && (
              <a href={cta.link}
                className="hidden lg:flex items-center gap-2 border border-white/25 hover:border-white
                  px-4 py-[7px] text-[11px] tracking-[0.2em] uppercase text-gray-300 hover:text-black
                  hover:bg-white transition-all duration-300 group">
                <img alt="" className="w-[15px] opacity-60 group-hover:opacity-100 group-hover:invert transition-all"
                  src={donate} loading="eager" decoding="async" />
                <span>{cta.label}</span>
              </a>
            )}

            <button onClick={() => setMenuOpen(v => !v)} aria-label="Toggle menu"
              className="lg:hidden flex flex-col justify-center items-center w-9 h-9 gap-[5px]">
              <span className={`block h-[1.5px] bg-white transition-all duration-300 origin-center
                ${menuOpen ? 'w-6 rotate-45 translate-y-[6.5px]' : 'w-6'}`} />
              <span className={`block h-[1.5px] bg-white transition-all duration-200
                ${menuOpen ? 'w-0 opacity-0' : 'w-4 self-end'}`} />
              <span className={`block h-[1.5px] bg-white transition-all duration-300 origin-center
                ${menuOpen ? 'w-6 -rotate-45 -translate-y-[6.5px]' : 'w-6'}`} />
            </button>
          </div>
        </div>

        {isScrolled && (
          <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        )}
      </header>

      {/* Mobile Drawer */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-400 ${menuOpen ? 'visible' : 'invisible'}`}>
        <div onClick={() => setMenuOpen(false)}
          className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${menuOpen ? 'opacity-100' : 'opacity-0'}`} />
        <div className={`absolute top-0 right-0 h-full w-[280px] bg-[#0a0a0a] border-l border-white/[0.06]
          flex flex-col transition-transform duration-400 ease-in-out ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between px-7 py-5 border-b border-white/[0.06]">
            <Link to="/" onClick={() => setMenuOpen(false)}>
              <img alt="Elayone Music" className="w-[46px] invert" src={logo} />
            </Link>
            <button onClick={() => setMenuOpen(false)} className="text-gray-400 hover:text-white transition-colors p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="flex flex-col px-7 pt-8 gap-1 flex-1">
            {NAV_LINKS.map(({ link, label, menu_key }, i) => {
              const isActive = location.pathname === link;
              return (
                <Link key={menu_key} to={link}
                  className={`flex items-center justify-between py-4 text-[12px] tracking-[0.18em] uppercase
                    border-b border-white/[0.05] transition-colors duration-200
                    ${isActive ? 'text-white' : 'text-gray-500 hover:text-white'}`}
                  style={{ transitionDelay: menuOpen ? `${i * 40}ms` : '0ms' }}>
                  {label}
                  {isActive && <span className="w-1 h-1 rounded-full bg-white" />}
                </Link>
              );
            })}
            <div className="flex items-center justify-between py-4 text-[12px] tracking-[0.18em] uppercase text-gray-500">
              <span>Appearance</span>
              <ThemeToggle style={{ color: 'rgba(255,255,255,0.7)' }} />
            </div>
          </nav>
          {cta && (
            <div className="px-7 py-8">
              <a href={cta.link}
                className="flex items-center justify-center gap-2 w-full border border-white/20
                  py-3 text-[11px] tracking-[0.2em] uppercase text-gray-300
                  hover:text-black hover:bg-white hover:border-white transition-all duration-300">
                <img alt="" className="w-[14px] opacity-60" src={donate} />
                {cta.label}
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Header;